<template>
  <div class="px-6 lg:px-10 py-6">
    <h1 class="text-[26px] font-bold text-gray-900 mb-8">Account Tree Report</h1>

    <div v-if="!canView" class="mt-10"></div>

    <DynamicReportForm
      v-else
      v-model="model"
      :fields="fields"
      :columns="2"
      stackAt="md"
      contentWidth="2xl"
      actionLabel="View Report"
      :showTitle="false"
      :loading="loading"
      :errors="errors"
      @submit="onSubmit"
    />
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from "vue"
import DynamicReportForm from "@/components/DynamicReportForm.vue"
import api from "@/api"
import Swal from "sweetalert2"
import { loadMyPermissions, hasAny } from "@/utils/permissions"

const canView = ref(false)
const loading = ref(false)
const errors  = ref({})

onMounted(async () => {
  await loadMyPermissions(false)
  canView.value = hasAny(["account_tree_report.view"])
})

const model = ref({
  account_tree:       "",
  account_tree_child: "",
  company_name:       "",
  from_date:          "",
  to_date:            "",
})

async function fetchChildren(parentId) {
  if (!parentId) return []
  try {
    const { data } = await api.get(`/accounting/accounting-tree/${parentId}/children`)
    return (data?.children ?? []).map((c) => ({ label: c.name, value: c.id }))
  } catch (e) {
    return []
  }
}

// fields reactive عشان تعديل child.options يشغّل الـ watch تبع الكومبوننت
const fields = reactive([
  {
    name:        "account_tree",
    label:       "Account Tree",
    type:        "select",
    span:        "full",
    placeholder: "Select  Account Tree",
    loader: async () => {
      try {
        const { data } = await api.get("/accounting/accounting-tree/parents")
        return (data?.accounts ?? []).map((n) => ({ label: n.name, value: n.id }))
      } catch (e) {
        console.error("Error fetching account tree:", e)
        return []
      }
    },
  },
  {
    name:        "account_tree_child",
    label:       "Account Tree Child",
    type:        "select",
    span:        "full",
    placeholder: "Select  Account Tree Child",
    options: [], // بتتعبّى reactive من الـ watch تحت
  },
  {
    name:        "company_name",
    label:       "Company Name",
    type:        "select",
    span:        "full",
    placeholder: "Select  company",
    loader: async () => {
      try {
        const { data } = await api.get("/accounting/companies")
        return (data ?? []).map((c) => ({ label: c.name, value: c.id }))
      } catch (e) {
        console.error("Error fetching companies:", e)
        return []
      }
    },
  },
  { name: "from_date", label: "From", type: "date", span: 1, placeholder: "yyyy/mm/dd" },
  { name: "to_date",   label: "To",   type: "date", span: 1, placeholder: "yyyy/mm/dd" },
])

// اول ما يتغيّر الـ parent: نفضّي الـ child المختار ونحدّث options تبعه
watch(
  () => model.value.account_tree,
  async (val) => {
    model.value.account_tree_child = ""
    const child = fields.find((f) => f.name === "account_tree_child")
    child.options = await fetchChildren(val)
  }
)

// نمسح خطأ أي field اول ما ينتعبّى
watch(model, (v) => {
  if (!Object.keys(errors.value).length) return
  const e = { ...errors.value }
  if (v.account_tree) delete e.account_tree
  if (v.company_name) delete e.company_name
  if (v.from_date)    delete e.from_date
  if (v.to_date)      delete e.to_date
  if (v.from_date && v.to_date && v.from_date <= v.to_date) delete e.to_date
  errors.value = e
}, { deep: true })

function validate(payload) {
  const e = {}
  if (!payload.account_tree) e.account_tree = "Please select an account tree"
  if (!payload.company_name) e.company_name = "Please select a company"
  if (!payload.from_date)    e.from_date    = "Please select a start date"
  if (!payload.to_date)      e.to_date      = "Please select an end date"
  if (payload.from_date && payload.to_date && payload.from_date > payload.to_date) {
    e.to_date = "End date must be after the start date"
  }
  errors.value = e
  return Object.keys(e).length === 0
}

async function onSubmit(payload) {
  if (!canView.value) return
  if (loading.value) return
  if (!validate(payload)) return

  const body = {
    account_tree:  payload.account_tree,               // parent id
    child_account: payload.account_tree_child || null, // child id (optional)
    company_id:    payload.company_name,               // company id
    from:          payload.from_date,
    to:            payload.to_date,
  }

  loading.value = true
  try {
    const res = await api.post("/accounting/accounting-tree/report", body, {
      responseType: "blob",
    })
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))
    window.open(url, "_blank")
  } catch (err) {
    console.error("Failed to generate report:", err)
    Swal.fire({
      icon: "error",
      title: "Failed to generate report",
      toast: true,
      position: "top",
      timer: 2000,
      showConfirmButton: false,
    })
  } finally {
    loading.value = false
  }
}
</script>
